'use strict'

// Utils
import {createScapiFetch} from './utils'

/**
 * Manages the URL lifecycle for categories and products by interacting with a specified API endpoint.
 *
 * This class provides methods to handle the URL lifecycle for categories and products, utilizing a fetch function
 * created with the provided configuration. It supports asynchronous operations to communicate with the API and 
 * retrieve lifecycle data.
 *
 * @class UrlLifeCycleMgr
 * @param {Object} config - The configuration object used to initialize the instance.
 * @property {Object} config - Stores the configuration object for the instance.
 * @property {Function} fetch - A function created by `createScapiFetch` using the provided configuration.
 *
 * @method async categoryUrlLifeCycle
 * Asynchronously handles the URL lifecycle for a category by making a request to the specified endpoint.
 *
 * @param {string} siteId - The identifier for the site for which the URL lifecycle is being managed.
 * @param {string} accessToken - The access token used for authentication in the API request.
 * @param {Object} [queryParams={}] - Optional query parameters for the request.
 * @param {string} [queryParams.categoryId] - The category ID to be included in the query string.
 * @returns {Promise<Object>} A promise that resolves to the response from the URL lifecycle API, or an empty object if an error occurs.
 *
 * @method async productUrlLifeCycle
 * Asynchronously handles the product URL lifecycle by making a request to the specified endpoint.
 *
 * @param {string} siteId - The identifier for the site for which the URL lifecycle is being managed.
 * @param {string} accessToken - The access token used for authentication in the API request.
 * @param {Object} [queryParams={}] - Optional query parameters for the request.
 * @param {string} [queryParams.productId] - The product ID to be included in the query string.
 * @returns {Promise<Object>} A promise that resolves to the response from the URL lifecycle API, or an empty object in case of an error.
 *
 * @throws Will return an empty object if the fetch operation fails.
 */
class UrlLifeCycleMgr {
    /**
     * Constructs an instance of the class with the provided configuration.
     * Initializes the fetch method using the given configuration.
     *
     * @constructor
     * @param {Object} config - The configuration object used to initialize the instance.
     * @property {Object} config - Stores the configuration object for the instance.
     * @property {Function} fetch - A function created by `createScapiFetch` using the provided configuration.
     */
    constructor(config) {
        this.config = config
        this.fetch = createScapiFetch(config)
    }

    /**
     * Asynchronously handles the URL lifecycle for a category by making a request to the specified endpoint.
     *
     * @async
     * @param {string} siteId - The identifier for the site for which the URL lifecycle is being managed.
     * @param {string} accessToken - The access token used for authentication in the API request.
     * @param {Object} [queryParams={}] - Optional query parameters for the request.
     * @param {string} [queryParams.categoryId] - The category ID to be included in the query string.
     * @returns {Promise<Object>} A promise that resolves to the response from the URL lifecycle API, or an empty object if an error occurs.
     */
    async categoryUrlLifeCycle(siteId, accessToken, queryParams = {}) {
        let endpoint = 'category'
        let method = 'GET'
        let methodName = 'category'
        let requestParams = {
            body: null,
            apiPath: 'url-lifecycle',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId
        }
        let apiVersion = 'v1'
        let urlLifeCycleResponse
        let urlParam = {
            querystring: {
                c_categoryId: queryParams.categoryId
            }
        }

        try {
            urlLifeCycleResponse = await this.fetch(
                endpoint,
                method,
                [urlParam],
                methodName,
                requestParams,
                apiVersion
            )

            return urlLifeCycleResponse || {}
        } catch (error) {
            return {}
        }
    }

    /**
     * Asynchronously handles the product URL lifecycle by making a request to the specified endpoint.
     *
     * @param {string} siteId - The identifier for the site for which the URL lifecycle is being managed.
     * @param {string} accessToken - The access token used for authentication in the API request.
     * @param {Object} [queryParams={}] - Optional query parameters for the request.
     * @param {string} [queryParams.productId] - The product ID to be included in the query string.
     * @returns {Promise<Object>} A promise that resolves to the response from the URL lifecycle API, or an empty object in case of an error.
     *
     * @throws Will return an empty object if the fetch operation fails.
     */
    async productUrlLifeCycle(siteId, accessToken, queryParams = {}) {
        let endpoint = 'product'
        let method = 'GET'
        let methodName = 'product'
        let requestParams = {
            body: null,
            apiPath: 'url-lifecycle',
            accessToken: accessToken,
            isCached: false,
            siteID: siteId
        }
        let apiVersion = 'v1'
        let urlLifeCycleResponse
        let urlParam = {
            querystring: {
                c_productId: queryParams.productId
            }
        }

        try {
            urlLifeCycleResponse = await this.fetch(
                endpoint,
                method,
                [urlParam],
                methodName,
                requestParams,
                apiVersion
            )

            return urlLifeCycleResponse || {}
        } catch (error) {
            return {}
        }
    }
}

export default UrlLifeCycleMgr
